import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  LogOut, LayoutDashboard, ListOrdered, PlusCircle, Settings as SettingsIcon,
  Brain, Stethoscope, ChevronDown, Menu, X, Crown, LifeBuoy, Radar,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";

// Iter 34 — Build version badge in the footer. Disambiguates Preview vs
// Production deploys (different backend SHAs surface here).
function BuildVersionBadge() {
  const [v, setV] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/health", { timeout: 6000 })
      .then((r) => { if (!cancelled) setV(r.data?.version || null); })
      .catch(() => { /* silent — badge just won't render */ });
    return () => { cancelled = true; };
  }, []);
  if (!v?.sha) return <span className="font-mono">v1.0</span>;
  const tone = v.env === "production" ? "var(--bt-win)" : "var(--bt-warn)";
  return (
    <span
      data-testid="build-version-badge"
      className="font-mono inline-flex items-center gap-1.5"
      title={`Backend started ${v.started_at}`}
    >
      <span
        aria-hidden="true"
        className="inline-block rounded-full"
        style={{ width: 6, height: 6, background: tone }}
      />
      <span className="uppercase tracking-wider text-[10px]" style={{ color: tone }}>
        {v.env}
      </span>
      <span>·</span>
      <span>{v.sha}</span>
    </span>
  );
}

// Iter 52 — Compact segmented control reused inside the Account dropdown
// for Language / Currency / Theme. Replaces the 3 separate inline pill
// toggles that used to crowd the navbar.
function SegmentedControl({ testid, options, value, onChange, label }) {
  return (
    <div className="px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-[0.15em] text-[var(--bt-text-muted)] mb-1.5 px-1">
        {label}
      </p>
      <div
        data-testid={testid}
        className="inline-flex w-full rounded-sm overflow-hidden border border-[var(--bt-border)]"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`${testid}-${String(opt.value).toLowerCase()}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(opt.value); }}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold tracking-wider transition-colors duration-150 ${
                active
                  ? "bg-[var(--bt-accent)] text-black"
                  : "bg-transparent text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Iter 52 — Account dropdown. Consolidates user identity + all preference
// toggles (Language, Currency, Theme) + Settings link + Logout into a single
// menu so the top navbar can keep primary navigation uncompressed.
function AccountMenu({ onLogout }) {
  const { user } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const name = user && typeof user === "object" ? user.name : "Guest";
  const email = user && typeof user === "object" ? user.email : "";
  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="account-menu-trigger"
          className="inline-flex items-center gap-2 px-2 py-1.5 rounded-sm border border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] transition-colors duration-150"
        >
          <span
            aria-hidden="true"
            className="w-7 h-7 rounded-sm bg-[var(--bt-accent)] text-black font-heading text-xs font-bold flex items-center justify-center"
          >
            {initials || "??"}
          </span>
          <span
            data-testid="account-menu-username"
            className="hidden sm:inline text-sm font-medium text-[var(--bt-text)] max-w-[120px] truncate"
          >
            {name}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-[var(--bt-text-muted)]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="account-menu-content"
        className="w-64 bg-[var(--bt-surface)] border-[var(--bt-border)]"
      >
        <DropdownMenuLabel className="flex flex-col gap-0.5 pb-2">
          <span className="font-semibold text-[var(--bt-text)] truncate">{name}</span>
          {email && (
            <span className="text-xs text-[var(--bt-text-muted)] font-normal truncate">{email}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[var(--bt-border)]" />

        <SegmentedControl
          testid="pref-language"
          label="Language"
          value={language}
          onChange={setLanguage}
          options={[{ value: "en", label: "EN" }, { value: "hi", label: "HI" }]}
        />
        <SegmentedControl
          testid="pref-currency"
          label="Currency"
          value={currency}
          onChange={setCurrency}
          options={[{ value: "USD", label: "$ USD" }, { value: "INR", label: "₹ INR" }]}
        />
        <SegmentedControl
          testid="pref-theme"
          label="Theme"
          value={theme}
          onChange={setTheme}
          options={[{ value: "dark", label: "🌙 Dark" }, { value: "light", label: "☀️ Light" }]}
        />

        <DropdownMenuSeparator className="bg-[var(--bt-border)]" />
        <DropdownMenuItem asChild className="cursor-pointer focus:bg-[var(--bt-surface-hover)]">
          <Link to="/settings" data-testid="account-menu-settings-link" className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="account-menu-logout"
          onClick={onLogout}
          className="cursor-pointer focus:bg-[var(--bt-surface-hover)] text-[var(--bt-danger)] focus:text-[var(--bt-danger)]"
        >
          <LogOut className="w-4 h-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { logout } = useAuth();
  const nav = useNavigate();
  // Iter 52 — Mobile drawer state for the hamburger menu (≤ md screens).
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 text-sm font-medium tracking-tight transition-colors duration-150 border-b-2 whitespace-nowrap ${
      isActive
        ? "text-[var(--bt-text)] border-[var(--bt-accent)]"
        : "text-[var(--bt-text-muted)] border-transparent hover:text-[var(--bt-text)]"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-base font-medium border-l-2 transition-colors duration-150 ${
      isActive
        ? "text-[var(--bt-text)] border-[var(--bt-accent)] bg-[var(--bt-surface-hover)]"
        : "text-[var(--bt-text-muted)] border-transparent hover:bg-[var(--bt-surface-hover)]"
    }`;

  return (
    <div className="min-h-screen bt-grid-bg overflow-x-hidden">
      <header
        data-testid="top-nav"
        className="bt-nav sticky top-0 z-50 bt-glass border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          {/* Logo — fixed size, never shrinks */}
          <Link
            to="/dashboard"
            data-testid="nav-logo"
            className="flex items-center gap-2.5 group flex-none"
          >
            <span className="w-8 h-8 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center">
              <Brain className="w-4 h-4 text-black" strokeWidth={2.5} />
            </span>
            <span className="font-heading text-lg tracking-tight">
              <span className="bt-logo-brain">Brain</span><span className="bt-logo-trades text-[var(--bt-accent)]">TRADES</span>
            </span>
          </Link>

          {/* Primary nav — desktop only. `flex-none` so it never compresses;
              parent uses `gap-4` not `justify-between` to keep this glued to logo. */}
          <nav
            data-testid="nav-primary"
            className="bt-nav hidden lg:flex items-center gap-1 flex-none"
          >
            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </NavLink>
            <NavLink to="/trades" className={navLinkClass} data-testid="nav-trades">
              <ListOrdered className="w-4 h-4" /> Trades
            </NavLink>
            <NavLink to="/trades/new" className={navLinkClass} data-testid="nav-add-trade">
              <PlusCircle className="w-4 h-4" /> <span data-testid="nav-add-trade-label">+ Log Trade</span>
            </NavLink>
            <NavLink to="/trade-doctor" className={navLinkClass} data-testid="nav-trade-doctor">
              <Stethoscope className="w-4 h-4" /> Trade Doctor
            </NavLink>
            <NavLink to="/coach" className={navLinkClass} data-testid="nav-coach">
              <Radar className="w-4 h-4" /> Coach
            </NavLink>
            <NavLink to="/support" className={navLinkClass} data-testid="nav-support">
              <LifeBuoy className="w-4 h-4" /> Support
            </NavLink>
            <NavLink to="/pricing" className={navLinkClass} data-testid="nav-pricing">
              <Crown className="w-4 h-4 text-[var(--bt-accent)]" /> Upgrade
            </NavLink>
          </nav>

          {/* Spacer pushes account menu to the right */}
          <div className="flex-1" />

          {/* Hamburger — visible only when primary nav is hidden (<lg) */}
          <button
            type="button"
            data-testid="nav-hamburger"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-sm border border-[var(--bt-border)] text-[var(--bt-text)] hover:bg-[var(--bt-surface-hover)]"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>

          {/* Account dropdown — sole right-side element. Replaces 3 inline
              toggles + standalone username + logout button. */}
          <AccountMenu onLogout={onLogout} />
        </div>

        {/* Mobile primary-nav drawer — only renders below the lg breakpoint
            and only when the hamburger is open. The desktop primary nav is
            fully hidden in this range so no squishing can occur. */}
        {mobileOpen && (
          <nav
            data-testid="nav-mobile-drawer"
            className="lg:hidden border-t border-[var(--bt-border)] bg-[var(--bt-surface)] flex flex-col"
          >
            <NavLink to="/dashboard" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </NavLink>
            <NavLink to="/trades" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-trades">
              <ListOrdered className="w-4 h-4" /> Trades
            </NavLink>
            <NavLink to="/trades/new" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-add-trade">
              <PlusCircle className="w-4 h-4" /> + Log Trade
            </NavLink>
            <NavLink to="/trade-doctor" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-trade-doctor">
              <Stethoscope className="w-4 h-4" /> Trade Doctor
            </NavLink>
            <NavLink to="/coach" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-coach">
              <Radar className="w-4 h-4" /> Coach
            </NavLink>
            <NavLink to="/support" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-support">
              <LifeBuoy className="w-4 h-4" /> Support
            </NavLink>
            <NavLink to="/pricing" className={mobileLinkClass} onClick={() => setMobileOpen(false)} data-testid="nav-mobile-pricing">
              <Crown className="w-4 h-4 text-[var(--bt-accent)]" /> Upgrade
            </NavLink>
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 bt-fade-up">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--bt-border)] mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between text-xs text-[var(--bt-text-muted)] gap-2">
          <span className="uppercase tracking-[0.25em]">© BrainTRADES — Journal smarter</span>
          <BuildVersionBadge />
        </div>
      </footer>
    </div>
  );
}
